import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { SelectField } from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Banner, Stack } from '@optimus-web/core';
import { ConfigureSsoPanelFieldNames, ConfigureSsoPanelFormData } from '../useConfigureSsoPanelForm';
import { ConfigureSsoPanelEnforcedDomainsTable } from './ConfigureSsoPanelEnforcedDomainsTable';

export function ConfigureSsoPanelDomains() {
    const { data, isLoading } = useRepositoryQuery('ssoConnection', 'getDomains', {});
    const domains = data?.domains ?? [];
    const { activeDomains, enforcedDomains, handleEnforcedDomainsChange } = useUserDomainsHandler();
    return (
        <Stack spacing="200" vertical>
            <SelectField
                label={<LocalizedText textKey={TextKey.UserDomains} />}
                loader={isLoading}
                size="medium"
                name={ConfigureSsoPanelFieldNames.Domains}
                required
                multiselect
                options={domains.map(renderOptionListItems)}
                helper={<LocalizedText textKey={TextKey.UserDomainsHelper} />}
            />

            {activeDomains.length > 0 && (
                <>
                    <Banner
                        primaryText={<LocalizedText textKey={TextKey.SsoSetupDomainBannerTitle} />}
                        variant="info"
                    />
                    <ConfigureSsoPanelEnforcedDomainsTable
                        activeDomains={activeDomains}
                        enforcedDomains={enforcedDomains}
                        onChange={handleEnforcedDomainsChange}
                    />
                </>
            )}
        </Stack>
    );
}

function useUserDomainsHandler() {
    const { watch, setValue } = useFormContext<ConfigureSsoPanelFormData>();
    const enforcedDomains = watch(ConfigureSsoPanelFieldNames.EnforcedDomains);
    const activeDomains = watch(ConfigureSsoPanelFieldNames.Domains);
    const handleEnforcedDomainsChange = useCallback(
        (newEnforcedDomains: Record<string, boolean>) =>
            setValue(ConfigureSsoPanelFieldNames.EnforcedDomains, newEnforcedDomains, { shouldDirty: true }),
        [setValue]
    );

    useEffect(() => {
        // We need to ensure that by default domains are enforced
        const shouldUpdateEnforcedDomains =
            activeDomains.some((domain) => enforcedDomains[domain] === undefined) ||
            Object.entries(enforcedDomains).length !== activeDomains.length;

        if (shouldUpdateEnforcedDomains) {
            const newEnforcedDomains = {} as Record<string, boolean>;
            activeDomains.forEach((domain) => {
                const currentStatus = enforcedDomains[domain];
                newEnforcedDomains[domain] = currentStatus === undefined ? true : currentStatus;
            });
            handleEnforcedDomainsChange(newEnforcedDomains);
        }
    }, [activeDomains, enforcedDomains, handleEnforcedDomainsChange]);

    return {
        enforcedDomains,
        activeDomains,
        handleEnforcedDomainsChange,
    };
}

const renderOptionListItems = (value: string) =>
    createOption({
        value,
        label: value,
    });
